import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Button,
    Grid,
    TextField,
    Typography,
    Divider,
    Paper,
    Box,
    Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useAddVehicleMutation } from "store/services/vehicleApi";
import {
    useLazyGetDriversByVehicleQuery,
    useLazyGetDriverContactAndBankQuery,
    useUpdateDriverContactAndBankMutation,
} from "store/services/driverApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import VehicleForm from "../../Vehicle/vehicleComponents/VehicleForm";
import VehicleHiringEntryDatesPicker from "./VehicleHiringEntryDatesPicker";

const HIRING_TYPE_OPTIONS = ["Daily", "Monthly", "One Way", "Round Trip"];

const INITIAL_STATE = {
    account: null,
    entity: null,
    sourceBranch: null,
    destinationBranch: null,
    hiringDates: [],
    hiringFare: "",
    purposeOfHiring: "",
    hiringType: null,
    vehicleType: null,
    vehicleRegistrationNumber: null,
    ownerName: "",
    ownerNumber: "",
    driver: null, // Selected driver (object with _id + name)
    driverName: "",
    driverNumber: "",
    bankAccountDetails: { bankName: "", accountName: "", accountNumber: "" },
    isActive: true,
    responsibleUser: null,
};

// Small, reusable section header used to visually group related fields.
const SectionHeader = ({ title, subtitle }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
            {title}
        </Typography>
        {subtitle && (
            <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
        )}
        <Divider sx={{ mt: 1 }} />
    </Box>
);

const sectionPaperSx = {
    p: { xs: 2, sm: 3 },
    mb: 3,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
};

const VehicleHiringEntryForm = ({
    isBusy = false,
    entryDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();

    // RTK Queries
    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
    const [fetchBranches, { data: branches, isLoading: loadingBranches }] = useLazyGetDDLBranchListQuery();
    const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] = useLazyGetDDLVehicleListQuery();
    const [fetchUsers, { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers }] = useLazyGetDDLSystemUserListQuery();
    const [addVehicle, { isLoading: isCreatingVehicle }] = useAddVehicleMutation();

    // Drivers tied to the selected vehicle + driver contact detail + mutation
    const [fetchVehicleDrivers, { data: vehicleDrivers, isFetching: loadingVehicleDrivers }] =
        useLazyGetDriversByVehicleQuery();
    const [fetchDriverContactAndBank, { data: selectedDriverDetail }] =
        useLazyGetDriverContactAndBankQuery();
    const [updateDriverContactAndBank, { isLoading: isUpdatingDriver }] =
        useUpdateDriverContactAndBankMutation();

    const [isVehicleModalActive, setIsVehicleModalActive] = React.useState(false);

    // While the vehicle modal is open, VehicleForm's own Cancel button calls
    // navigate(-1), which we can't prevent without editing VehicleForm. Instead,
    // push a throwaway history entry when the modal opens, so that "back" lands
    // back where we started (no real navigation) and we close the modal instead.
    useEffect(() => {
        if (!isVehicleModalActive) return;
        window.history.pushState({ vehicleModal: true }, "");
        const handlePopState = () => {
            setIsVehicleModalActive(false);
        };
        window.addEventListener("popstate", handlePopState);
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [isVehicleModalActive]);

    const { data: vehicleTypeList = [] } = useListListQuery({
        type: ENTITY_LIST_CONSTANTS.VEHICLE_TYPES,
        status: "Active",
    });

    // React Hook Form
    const {
        control,
        handleSubmit,
        setValue,
        reset,
        setError,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || null,
            account: viewEntity?.entity?.account || null,
        },
        mode: "onChange",
    });

    const _watchAccount = useWatch({ control, name: "account" });
    const _watchEntity = useWatch({ control, name: "entity" });
    const isListsLoading = false;
    const _watchHiringType = useWatch({ control, name: "hiringType" });
    const _watchHiringFare = useWatch({ control, name: "hiringFare" });
    const _watchHiringDates = useWatch({ control, name: "hiringDates" });
    const _watchVehicle = useWatch({ control, name: "vehicleRegistrationNumber" });
    const _watchDriver = useWatch({ control, name: "driver" });
    const _watchDriverNumber = useWatch({ control, name: "driverNumber" });
    const _watchBankAccountDetails = useWatch({ control, name: "bankAccountDetails" });
    const _watchOwnerName = useWatch({ control, name: "ownerName" });
    const _watchOwnerNumber = useWatch({ control, name: "ownerNumber" });

    // Vehicle Type is derived from the selected Vehicle's own vehicleType
    useEffect(() => {
        setValue("vehicleType", _watchVehicle?.vehicleType?.name || null);
    }, [_watchVehicle, setValue]);

    // Tracks which vehicle's drivers are currently loaded. Used by the
    // auto-select useEffect to avoid picking from a stale list (e.g.
    // when the user switches vehicles faster than the fetch completes).
    const activeDriversVehicleRef = React.useRef(null);

    // When vehicle changes, clear all derived driver fields AND fetch the
    // new vehicle's drivers. `vehicleType` is intentionally NOT cleared
    // here — it has its own derivation effect above that re-runs on
    // `_watchVehicle` change and would just get overwritten by this one,
    // leaving the field empty.
    //
    // Skipped on existing entryDetail (the saved driver is restored by
    // the reset() effect and we don't want to wipe it).
    useEffect(() => {
        if (entryDetail) return;
        const vehicleId = _watchVehicle?._id;

        // Reset driver-derived fields when vehicle selection changes.
        setValue("driver", null);
        setValue("driverName", "");
        setValue("driverNumber", "");
        setValue("ownerName", "");
        setValue("ownerNumber", "");
        setValue("bankAccountDetails", {
            bankName: "",
            accountName: "",
            accountNumber: "",
        });

        if (!vehicleId) return;
        // Stamp before kicking off so the auto-select won't run for a
        // stale drivers list (loadingVehicleDrivers will be true until
        // the response lands; the stamp records which vehicle's data we
        // expect to receive).
        activeDriversVehicleRef.current = vehicleId;
        fetchVehicleDrivers(vehicleId);
    }, [_watchVehicle?._id, fetchVehicleDrivers, setValue, entryDetail]);

    // Auto-pick the first driver of the SELECTED vehicle so its contact
    // + bank details populate the form immediately. Skipped on existing
    // entryDetail (the saved driver is restored by reset()), when no
    // vehicle is selected, while a fetch is still in flight, or when
    // the loaded `vehicleDrivers` belongs to a different (stale) vehicle.
    useEffect(() => {
        if (!_watchVehicle?._id) return;
        if (entryDetail) return;
        if (loadingVehicleDrivers) return;
        if (activeDriversVehicleRef.current !== _watchVehicle._id) return;
        if (!vehicleDrivers || vehicleDrivers.length === 0) return;
        if (_watchDriver && vehicleDrivers.some((d) => d._id === _watchDriver._id)) {
            return;
        }
        setValue("driver", vehicleDrivers[0]);
    }, [vehicleDrivers, loadingVehicleDrivers, entryDetail, setValue, _watchDriver, _watchVehicle?._id]);

    // True when the currently-selected driver record carries NO phone — used
    // to decide whether to show the "Add to Driver" button. Once the user
    // saves the number, this flips to false on the next refetch and the
    // button disappears.
    const driverHasPhone = Boolean(
        selectedDriverDetail?.ownerphone || selectedDriverDetail?.phone,
    );

    // True when the currently-selected driver record carries NO owner
    // contact info. Used to decide whether to surface a button that
    // pushes the form's owner values back to the driver.
    const driverHasOwner = Boolean(
        selectedDriverDetail?.ownerName || selectedDriverDetail?.ownerphone,
    );

    // True when the driver's record carries NO bank account details.
    // Drives the "Add Bank Details to Driver" button visibility — the
    // button stays visible while the user is typing in the form, and
    // only disappears after the values are actually saved to the driver.
    const driverHasBank = Boolean(
        selectedDriverDetail?.bankAccountDetails?.bankName ||
        selectedDriverDetail?.bankAccountDetails?.accountName ||
        selectedDriverDetail?.bankAccountDetails?.accountNumber,
    );

    // When driver changes, fetch their contact + bank details so the
    // form auto-populates from the Driver model.
    useEffect(() => {
        const driverId = _watchDriver?._id;
        if (!driverId) {
            return;
        }
        fetchDriverContactAndBank(driverId);
    }, [_watchDriver?._id, fetchDriverContactAndBank]);

    // When driver detail arrives, populate driver phone + bank details.
    // Driver Number is sourced from the driver's `phone` (not `ownerphone`)
    // so that Driver Number and Owner Number stay distinct when the driver
    // has different values in both fields. Falls back to ownerphone only
    // when there's no `phone` set (legacy records). Owner Name + Owner
    // Number use ONLY ownerName / ownerphone — if those are missing on the
    // driver record, the fields stay empty (so a button can be surfaced to
    // save them back).
    useEffect(() => {
        if (!selectedDriverDetail) return;
        // Driver Number: prefer phone; fall back to ownerphone for legacy
        // records that only have ownerphone.
        const driverPhone =
            selectedDriverDetail.phone || selectedDriverDetail.ownerphone || "";
        // Owner Name / Owner Number: ONLY ownerName / ownerphone.
        const ownerName = selectedDriverDetail.ownerName || "";
        const ownerNumber = selectedDriverDetail.ownerphone || "";
        const bank = selectedDriverDetail.bankAccountDetails || {};
        setValue("driverName", selectedDriverDetail.name || "");
        setValue("ownerName", ownerName);
        setValue("ownerNumber", ownerNumber);
        setValue("driverNumber", driverPhone);
        setValue("bankAccountDetails", {
            bankName: bank.bankName || "",
            accountName: bank.accountName || "",
            accountNumber: bank.accountNumber || "",
        });
    }, [selectedDriverDetail, setValue]);

    // Live total fare preview (server recomputes authoritative value on save)
    const _previewTotalFare = useMemo(() => {
        const fare = Number(_watchHiringFare) || 0;
        if (_watchHiringType !== "Daily") return fare;
        const totalDays = _watchHiringDates?.length || 0;
        const absentCount = entryDetail?.absentDates?.length || 0;
        const workingDays = Math.max(totalDays - absentCount, 0);
        return totalDays > 0 ? fare * workingDays : 0;
    }, [_watchHiringType, _watchHiringFare, _watchHiringDates, entryDetail]);

    // Handle Form Submission
    const handleOnSubmit = (data) => {
        // Manual validation for driverNumber ONLY on Add Entry
        if (!entryDetail && data.driverNumber && !/^[0-9]{10}$/.test(data.driverNumber)) {
            setError("driverNumber", {
                type: "manual",
                message: "Driver Number must be exactly 10 digits.",
            });
            return; // Prevent form submission
        }
        // Note: `driver` is an internal form-only field used to look up
        // the driver's contact/bank details. The saved entry keeps the
        // denormalized driverName + driverNumber + bankAccountDetails
        // (matching the existing VehicleHiringEntry model schema).
        const { driver: _selectedDriver, ...rest } = data;
        onSubmit({
            ...rest,
            account: data?.account?._id,
            entity: data?.entity?._id,
            sourceBranch: data?.sourceBranch?._id,
            destinationBranch: data?.destinationBranch?._id,
            hiringType: data?.hiringType,
            vehicleType: data?.vehicleType,
            vehicleRegistrationNumber: data?.vehicleRegistrationNumber?._id || "",
            responsibleUser: data?.responsibleUser?._id || null,
            bankAccountDetails: {
                bankName: data?.bankAccountDetails?.bankName || "",
                accountName: data?.bankAccountDetails?.accountName || "",
                accountNumber: data?.bankAccountDetails?.accountNumber || "",
            },
        });
    };

    // Save the in-form driver phone + bank details back to the Driver model.
    // Triggered from the "Add to Driver" buttons when the driver is selected
    // but their record lacks this info.
    //
    // Modes (the unused fields are omitted from the payload, so the
    // PATCH endpoint leaves existing values alone):
    //   - { savePhone: true }            — saves phone + bank details.
    //     Used by the Driver Number field's "Add to Driver".
    //   - { saveOwner: true }            — saves ownerName + ownerphone
    //     + bank details. Used by the Owner Number field's "Add to Driver".
    //     The driver's `phone` is intentionally NOT sent so the existing
    //     phone isn't overwritten with the owner's number.
    //   - no flags                      — saves bank details only.
    //     Used by "Add Bank Details to Driver".
    const handleSaveDriverContactAndBank = async ({
        savePhone = false,
        saveOwner = false,
    } = {}) => {
        if (!_watchDriver?._id) {
            toast.warning("Please select a driver first.");
            return;
        }
        const payload = {
            id: _watchDriver._id,
            bankAccountDetails: {
                bankName: _watchBankAccountDetails?.bankName || "",
                accountName: _watchBankAccountDetails?.accountName || "",
                accountNumber: _watchBankAccountDetails?.accountNumber || "",
            },
        };
        if (saveOwner) {
            payload.ownerName = _watchOwnerName || "";
            payload.ownerphone = _watchOwnerNumber || "";
            // No `phone` — keep the existing driver phone untouched.
        }
        if (savePhone) {
            payload.phone = _watchDriverNumber || "";
        }
        try {
            await updateDriverContactAndBank(payload).unwrap();
            toast.success("Driver details updated successfully.");
            // Re-fetch so the form reflects the updated state.
            fetchDriverContactAndBank(_watchDriver._id);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update driver details.");
        }
    };

    // Helper to resolve a list item for reset
    const handleVehicleModalForm = async (data) => {
        try {
            const newVehicle = await addVehicle(data).unwrap();
            toast.success("Vehicle Created Successfully!");
            const typeMatch = vehicleTypeList.find((t) => t._id === data.vehicleType);
            const formattedVehicle = {
                _id: newVehicle?._id,
                registration_number: newVehicle?.registration_number,
                vehicle_id: newVehicle?.vehicle_id,
                vehicleType: typeMatch ? { _id: typeMatch._id, name: typeMatch.name } : null,
            };
            setValue("vehicleRegistrationNumber", formattedVehicle);
            setIsVehicleModalActive(false);
            if (_watchEntity?._id) {
                fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity._id });
            }
        } catch (error) {
            toast.error(error?.data?.message ?? "Could Not Create Vehicle!");
        }
    };

    // Reset form when entryDetail changes
    const isMounted = useRef(true);
    useEffect(() => {
        if (!isMounted.current || isListsLoading) return;
        if (!entryDetail) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity || null,
                account: viewEntity?.entity?.account || null,
            });
            return;
        }
        reset({
            ...INITIAL_STATE,
            ...entryDetail,
            account: entryDetail.account || null,
            entity: entryDetail.entity || null,
            sourceBranch: entryDetail.sourceBranch || null,
            destinationBranch: entryDetail.destinationBranch || null,
            hiringType: entryDetail.hiringType || null,
            vehicleType: entryDetail.vehicleType || null,
            responsibleUser: entryDetail.responsibleUser
                ? { _id: entryDetail.responsibleUser._id, label: entryDetail.responsibleUser.name }
                : null,
            driver: entryDetail.driver
                ? (typeof entryDetail.driver === "object"
                    ? { _id: entryDetail.driver._id, name: entryDetail.driver.name, driverId: entryDetail.driver.driverId }
                    : null)
                : null,
            bankAccountDetails: {
                bankName: entryDetail.bankAccountDetails?.bankName || "",
                accountName: entryDetail.bankAccountDetails?.accountName || "",
                accountNumber: entryDetail.bankAccountDetails?.accountNumber || "",
            },
            vehicleRegistrationNumber: entryDetail.vehicleRegistrationNumber || null,
            hiringDates: entryDetail.hiringDates || [],
        });
        return () => {
            isMounted.current = false;
        };
    }, [entryDetail, reset, viewEntity, isListsLoading]);

    // Fetch entities when account changes
    useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            fetchEntities({
                account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
            });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

    // Fetch branches when entity changes
    useEffect(() => {
        if (!_watchEntity?._id) return;
        fetchBranches({ account: _watchAccount?._id, entity: _watchEntity._id });
    }, [_watchAccount?._id, _watchEntity?._id, fetchBranches, viewEntity]);

    // Fetch vehicles when entity changes
    useEffect(() => {
        if (!_watchEntity?._id) return;
        fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity._id });
    }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, viewEntity]);

    // Fetch Users on mount
    useEffect(() => {
        fetchUsers({});
    }, [fetchUsers]);

    // Memoize mapped users (excluding the logged-in user, who cannot approve their own entry)
    const _mappedUsers = useMemo(
        () =>
            userList
                ?.filter((user) => user._id !== loggedInUser?._id)
                .map((user) => ({ _id: user._id, label: user.name })) || [],
        [userList, loggedInUser?._id]
    );

    // Fetch entities and branches for editing if account/entity is pre-selected
    useEffect(() => {
        if (entryDetail?.account?._id && isMounted.current) {
            fetchEntities({ account: entryDetail.account._id });
            if (entryDetail?.entity?._id) {
                fetchBranches({ account: entryDetail.account._id, entity: entryDetail.entity._id });
            }
        }
    }, [entryDetail, fetchEntities, fetchBranches]);

    return (
        <AppForm loading={isBusy}>
            <AppForm.Content>
                {/* ---------- Account & Entity ---------- */}
                {(isAccountAdmin || isSystemAdmin) && (
                    <Paper sx={sectionPaperSx}>
                        <SectionHeader title="Account & Entity" />
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="account"
                                    control={control}
                                    rules={{ required: "Please select an account." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field}
                                            fullWidth
                                            label="Account"
                                            required
                                            size="small"
                                            value={field.value ?? null}
                                            onChange={(_, data) => {
                                                field.onChange(data);
                                                setValue("entity", null);
                                                setValue("sourceBranch", null);
                                                setValue("destinationBranch", null);
                                            }}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                            options={accounts || []}
                                            loading={loadingAccounts}
                                            loadingText="Loading Account..."
                                            disabled={isAccountAdmin}
                                            helperText={errors?.account?.message}
                                            error={!!errors?.account}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="entity"
                                    control={control}
                                    rules={{ required: "Please select an entity." }}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            required
                                            size="small"
                                            label="Entity"
                                            value={value ?? null}
                                            onChange={(_, data) => {
                                                onChange(data);
                                                setValue("sourceBranch", null);
                                                setValue("destinationBranch", null);
                                            }}
                                            getOptionLabel={(option) => option?.name ?? ""}
                                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                            options={entities || []}
                                            loading={loadingEntities}
                                            loadingText="Loading Entities..."
                                            helperText={errors?.entity?.message}
                                            error={!!errors?.entity}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* ---------- Route ---------- */}
                <Paper sx={sectionPaperSx}>
                    <SectionHeader title="Route" subtitle="Where the vehicle picks up and drops off." />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="sourceBranch"
                                control={control}
                                rules={{ required: "Origin is required." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        required
                                        size="small"
                                        label="Origin (Source Branch)"
                                        value={value ?? null}
                                        onChange={(_, data) => onChange(data)}
                                        getOptionLabel={(option) => (option ? `${option?.name} - ${option?.code || ""}` : "")}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        options={branches || []}
                                        loading={loadingBranches}
                                        loadingText="Loading Branches..."
                                        helperText={errors?.sourceBranch?.message}
                                        error={!!errors?.sourceBranch}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="destinationBranch"
                                control={control}
                                rules={{ required: "Destination is required." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        required
                                        size="small"
                                        label="Destination Branch"
                                        value={value ?? null}
                                        onChange={(_, data) => onChange(data)}
                                        getOptionLabel={(option) => (option ? `${option?.name} - ${option?.code || ""}` : "")}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        options={branches || []}
                                        loading={loadingBranches}
                                        loadingText="Loading Branches..."
                                        helperText={errors?.destinationBranch?.message}
                                        error={!!errors?.destinationBranch}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* ---------- Hiring Schedule ---------- */}
                <Paper sx={sectionPaperSx}>
                    <SectionHeader title="Hiring Schedule" subtitle="Pick the dates and how the fare is calculated." />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Typography variant="body2" fontWeight={600} mb={1}>
                                Hiring Dates *
                            </Typography>
                            {/* Constrain the calendar's footprint so it doesn't dominate the row.
                                If VehicleHiringEntryDatesPicker exposes a size/density prop,
                                prefer that over this wrapper. */}
                            <Box sx={{ maxWidth: 320 }}>
                                <Controller
                                    name="hiringDates"
                                    control={control}
                                    rules={{ validate: (v) => (v && v.length > 0) || "Please select at least one hiring date." }}
                                    render={({ field: { value, onChange } }) => (
                                        <VehicleHiringEntryDatesPicker
                                            value={value}
                                            onChange={onChange}
                                            error={!!errors?.hiringDates}
                                            helperText={errors?.hiringDates?.message}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Stack spacing={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Controller
                                            name="hiringType"
                                            control={control}
                                            rules={{ required: "Hiring Type is required." }}
                                            render={({ field: { value, onChange } }) => (
                                                <BaseAutocomplete
                                                    fullWidth
                                                    required
                                                    size="small"
                                                    label="Hiring Type"
                                                    value={value ?? null}
                                                    onChange={(_, data) => onChange(data)}
                                                    getOptionLabel={(option) => option ?? ""}
                                                    isOptionEqualToValue={(option, value) => option === value}
                                                    options={HIRING_TYPE_OPTIONS}
                                                    helperText={errors?.hiringType?.message}
                                                    error={!!errors?.hiringType}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Controller
                                            name="hiringFare"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    type="number"
                                                    label="Hiring Fare"
                                                    size="small"
                                                    error={!!errors.hiringFare}
                                                    helperText={errors.hiringFare?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <TextField
                                    fullWidth
                                    disabled
                                    label="Total Fare (calculated)"
                                    size="small"
                                    value={_previewTotalFare}
                                    helperText={
                                        _watchHiringType === "Daily"
                                            ? "Fare × Working Days"
                                            : "Same as Hiring Fare"
                                    }
                                />
                                <Controller
                                    name="responsibleUser"
                                    control={control}
                                    rules={{ required: "Please select an Approve / Submitted To user." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            required
                                            size="small"
                                            label="Approve / Submitted To"
                                            loading={loadingUsers || fetchingUsers}
                                            loadingText="Loading Users..."
                                            options={_mappedUsers || []}
                                            value={field?.value || null}
                                            onChange={(_, data) => field.onChange(data)}
                                            getOptionLabel={(opt) => opt?.label || ""}
                                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                            helperText={errors?.responsibleUser?.message}
                                            error={!!errors?.responsibleUser}
                                        />
                                    )}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* ---------- Vehicle & Driver ---------- */}
                <Paper sx={sectionPaperSx}>
                    <SectionHeader title="Vehicle & Driver" subtitle="Who's driving and which vehicle is assigned." />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
                                <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Controller
                                        name="vehicleRegistrationNumber"
                                        control={control}
                                        rules={{ required: "Please select a vehicle." }}
                                        render={({ field: { value, onChange } }) => (
                                            <BaseAutocomplete
                                                fullWidth
                                                required
                                                size="small"
                                                label="Vehicle"
                                                value={value ?? null}
                                                onChange={(_, data) => onChange(data)}
                                                getOptionLabel={(option) =>
                                                    option ? `${option?.registration_number || ""} - ${option?.vehicle_id || ""}` : ""
                                                }
                                                isOptionEqualToValue={(option, value) =>
                                                    option?.registration_number === value?.registration_number
                                                }
                                                options={vehicles || []}
                                                loading={loadingVehicles}
                                                loadingText="Loading Vehicles..."
                                                disabled={loadingVehicles || !_watchEntity?._id}
                                                helperText={!_watchEntity?._id ? "Select an entity first." : errors?.vehicleRegistrationNumber?.message}
                                                error={!!errors?.vehicleRegistrationNumber}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item sx={{ pt: 0.5 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!_watchEntity?._id}
                                        onClick={() => setIsVehicleModalActive(true)}
                                    >
                                        Add New
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="vehicleType"
                                control={control}
                                render={({ field: { value } }) => (
                                    <TextField
                                        fullWidth
                                        disabled
                                        size="small"
                                        label="Vehicle Type"
                                        value={value || ""}
                                        helperText="Auto-filled from the selected Vehicle."
                                    />
                                )}
                            />
                        </Grid>


                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="driver"
                                key={_watchVehicle?._id ? `v-${_watchVehicle._id}` : "v-none"}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        size="small"
                                        label="Driver Name"
                                        value={value ?? null}
                                        onChange={(_, data) => onChange(data)}
                                        getOptionLabel={(option) =>
                                            option ? `${option?.name || ""}${option?.driverId ? ` (${option.driverId})` : ""}` : ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id
                                        }
                                        options={vehicleDrivers || []}
                                        loading={loadingVehicleDrivers}
                                        loadingText="Loading Drivers..."
                                        disabled={!_watchVehicle?._id}
                                        helperText={
                                            !_watchVehicle?._id
                                                ? "Select a vehicle first."
                                                : "Auto-fills phone + bank details from the Driver record."
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="driverNumber"
                                control={control}
                                rules={{ required: !entryDetail ? "Driver Number is required." : false }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        required={!entryDetail}
                                        fullWidth
                                        size="small"
                                        label="Driver Number"
                                        error={!!errors.driverNumber}
                                        helperText={
                                            errors.driverNumber?.message ||
                                            (_watchDriver?._id && !driverHasPhone
                                                ? "No phone on driver record. Add it and use 'Add to Driver' below."
                                                : "")
                                        }
                                        InputProps={{
                                            endAdornment: _watchDriver?._id && !driverHasPhone ? (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    disabled={isUpdatingDriver}
                                                    onClick={() =>
                                                        handleSaveDriverContactAndBank({ savePhone: true })
                                                    }
                                                >
                                                    Add to Driver
                                                </Button>
                                            ) : null,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="ownerName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Owner Name"
                                        helperText={
                                            _watchDriver?._id
                                                ? "Optional. From driver record when available."
                                                : "Optional."
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="ownerNumber"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Owner Number"
                                        helperText={
                                            _watchDriver?._id && !driverHasOwner && (_watchOwnerName || _watchOwnerNumber)
                                                ? "Not on driver record — use 'Add to Driver' to save."
                                                : _watchDriver?._id && !driverHasOwner
                                                    ? "Optional. Empty on driver record — type a value to enable save."
                                                    : "Optional."
                                        }
                                        InputProps={{
                                            endAdornment:
                                                _watchDriver?._id &&
                                                    !driverHasOwner &&
                                                    (_watchOwnerName || _watchOwnerNumber) ? (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        disabled={isUpdatingDriver}
                                                        onClick={() =>
                                                            handleSaveDriverContactAndBank({ saveOwner: true })
                                                        }
                                                    >
                                                        Add to Driver
                                                    </Button>
                                                ) : null,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* ---------- Purpose ---------- */}
                <Paper sx={sectionPaperSx}>
                    <SectionHeader title="Purpose of Hiring" />
                    <Controller
                        name="purposeOfHiring"
                        control={control}
                        rules={{ required: "Purpose of Hiring is required." }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                label="Purpose of Hiring"
                                error={!!errors.purposeOfHiring}
                                helperText={errors.purposeOfHiring?.message}
                            />
                        )}
                    />
                </Paper>

                {/* ---------- Bank Account Details ---------- */}
                <Paper sx={sectionPaperSx}>
                    <SectionHeader
                        title="Bank Account Details"
                        subtitle="Pre-filled from the Driver record when available."
                    />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="bankAccountDetails.bankName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Bank Name"
                                        helperText={errors?.bankAccountDetails?.bankName?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="bankAccountDetails.accountName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Bank Account Name"
                                        helperText={errors?.bankAccountDetails?.accountName?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="bankAccountDetails.accountNumber"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Account Number"
                                        helperText={errors?.bankAccountDetails?.accountNumber?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    {_watchDriver?._id && !driverHasBank && (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={isUpdatingDriver}
                                onClick={handleSaveDriverContactAndBank}
                            >
                                {isUpdatingDriver ? "Saving..." : "Add Bank Details to Driver"}
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* ---------- Status ---------- */}
                {entryDetail && (
                    <Paper sx={sectionPaperSx}>
                        <SectionHeader title="Status" />
                        <Controller
                            name="isActive"
                            control={control}
                            render={({ field }) => <AppSwitch {...field} label="Is Active?" />}
                        />
                    </Paper>
                )}
            </AppForm.Content>

            <BaseModal
                isOpen={isVehicleModalActive}
                onClose={() => {
                    if (isVehicleModalActive) window.history.back();
                }}
                title="Add Vehicle"
                width="800px"
                top="50%"
                left="58%"
            >
                <VehicleForm isBusy={isCreatingVehicle} onSubmit={handleVehicleModalForm} />
            </BaseModal>

            <AppForm.Actions>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnSubmit)}
                    disabled={isBusy || isListsLoading || loadingBranches || loadingVehicles}
                >
                    {entryDetail ? "Update Entry" : "Add Entry"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default VehicleHiringEntryForm;
